// BRK-S173 — a display label for a bond option whose WIRE VALUE is its label.
//
// ── THE PROBLEM ─────────────────────────────────────────────────────────────
//
// A bond-order select's options arrive as `string[]`, so the value IS the
// label: the obligee type is served as
//
//     ["Fed", "State", "County", "Municipality", "Private", "Other"]
//
// and a broker reads "Fed". The scoping document says **Federal**, and it is
// right — "Fed" is a data abbreviation, not a word anybody chooses from a
// dropdown.
//
// ── WHY THE FIX IS HERE AND NOT IN THE FIXTURE ──────────────────────────────
//
// The value is the CONTRACT. Discovery's configuration declares `Fed`, PMS
// validates the submitted bag against that configuration's snapshot, and stored
// orders carry `Fed`. "Improving" the spelling anywhere on the wire sends a
// value the configuration does not declare — which is precisely the class of
// mistake BRK-S141 cleaned up, when this repo's fixture had invented four key
// names the service never sends and the whole offline flow turned out to be a
// rehearsal of a configuration nobody serves.
//
// So this is DISPLAY ONLY, in the D-10 sense the currency and date conventions
// already establish: the string on screen changes, the string on the wire does
// not.
//
// ── WHY IT IS KEYED BY VALUE, AND HOW IT RETIRES ────────────────────────────
//
// Keyed by the option's own value, because that is all a bare `string[]` option
// carries — there is no field key, no path and no type on it to scope by.
//
// The safety property that makes that acceptable is the second rule below: a
// configuration-supplied `{value, label}` pair ALWAYS WINS. This table only
// ever fills in a label for an option that had none, so the day discovery
// publishes pairs for the obligee type — with whatever wording it chooses —
// this stops applying to that field on its own, with no release here. Delete
// the entry then; until then it cannot fight the contract, only stand in for
// its silence.
//
// It is a CONTRACT GAP, filed as one: the right home for an option's display
// label is the configuration that declares the option. See
// docs/bonds.md § Option labels.
import type { BondFieldOption } from "@/lib/brokerBonds";

/**
 * Wire value → the word a broker should read.
 *
 * Keep this SHORT. An entry here is a piece of vocabulary this client has
 * decided it knows better than the service does, and the honest fix for the
 * next one is a configuration change, not a second row.
 */
export const BOND_OPTION_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  // The obligee type's federal option (surety scoping document, 2026-09-21).
  Fed: "Federal",
};

/**
 * The configuration's options with a display label filled in wherever this
 * client has one and the configuration does not.
 *
 * Two rules, and the second is the one that matters:
 *
 *   1. A bare string option becomes `{value, label}` when the table names it —
 *      the VALUE is untouched, so what is saved and what is submitted are
 *      exactly what arrived.
 *   2. An option that already carries its own `label` is returned UNCHANGED,
 *      even when the table names its value. The configuration owns the wording;
 *      this table only speaks where the configuration is silent.
 *
 * `undefined` in, `undefined` out — the caller's "this field declares no
 * options" state is not this function's to invent.
 */
export function withBondOptionLabels(
  options: readonly BondFieldOption[] | undefined,
): BondFieldOption[] | undefined {
  if (options === undefined) return undefined;
  return options.map((option) => {
    if (typeof option !== "string") return option;
    const label = BOND_OPTION_DISPLAY_LABELS[option];
    return label === undefined ? option : { value: option, label };
  });
}
